<script setup lang="ts">
import { onMounted, ref, reactive, computed, watch } from 'vue'
import { NButton, NSpin, useMessage } from 'naive-ui'
import RouteShell from './RouteShell.vue'
import ListItemRow from '../components/ListItemRow.vue'
import CreateListModal from '../components/CreateListModal.vue'
import ListChips, { type ListChip } from '../components/ListChips.vue'
import ListItemComposer from '../components/ListItemComposer.vue'
import { useListItemsStore, type ListItem, type ListItemInput } from '../stores/lists'
import { useDocumentTitle } from '../composables/useDocumentTitle'

const store = useListItemsStore()
const message = useMessage()
useDocumentTitle('Lists')

const tab = ref<string>('')
const forms = reactive<Record<string, ListItemInput>>({})
const saving = reactive<Record<string, boolean>>({})
const editingItemId = ref<number | null>(null)
const draggingId = ref<number | null>(null)
const dragOverId = ref<number | null>(null)
const showCreateModal = ref(false)

function displayName(list: string): string {
  return list.replaceAll('_', ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase())
}

function getForm(list: string): ListItemInput {
  if (!forms[list]) forms[list] = { list, title: '', url: '', notes: '', priority: null }
  return forms[list]
}

const editingList = computed<string | null>(() => {
  if (editingItemId.value === null) return null
  return store.items.find((i) => i.id === editingItemId.value)?.list ?? null
})

function listItemsFor(list: string): ListItem[] {
  const all = store.byList(list)
  return [...all.filter((i) => !i.done), ...all.filter((i) => i.done)]
}

const chips = computed<ListChip[]>(() =>
  store.listNames.map((name) => ({
    name,
    label: displayName(name),
    openCount: store.byList(name).filter((i) => !i.done).length,
  })),
)

const selectedItems = computed<ListItem[]>(() => (tab.value ? listItemsFor(tab.value) : []))

// Select the first list once items load, and fall back to it whenever the
// selected list disappears (its last item removed).
watch(
  () => store.listNames,
  (names) => {
    if (names.length > 0 && !names.includes(tab.value)) tab.value = names[0]
  },
  { immediate: true },
)

onMounted(async () => {
  try {
    await store.fetchAll()
  } catch {
    /* loadError surfaces in the template */
  }
})

function cancelEdit(list: string) {
  editingItemId.value = null
  Object.assign(getForm(list), { title: '', url: '', notes: '', priority: null })
}

async function saveEdit(list: string) {
  const form = getForm(list)
  if (!form.title.trim()) {
    message.error('Title is required')
    return
  }
  if (editingItemId.value === null) return
  saving[list] = true
  try {
    await store.updateItem(editingItemId.value, {
      title: form.title,
      url: form.url || undefined,
      notes: form.notes || undefined,
      priority: form.priority || undefined,
    })
    cancelEdit(list)
    message.success('Item updated')
  } catch {
    message.error('Failed to update item')
  } finally {
    saving[list] = false
  }
}

async function addItem(list: string) {
  const form = getForm(list)
  if (!form.title.trim()) {
    message.error('Title is required')
    return
  }
  saving[list] = true
  try {
    await store.addItem({
      ...form,
      url: form.url || undefined,
      notes: form.notes || undefined,
      priority: form.priority || undefined,
    })
    Object.assign(form, { title: '', url: '', notes: '', priority: null })
    message.success('Item added')
  } catch {
    message.error('Failed to add item')
  } finally {
    saving[list] = false
  }
}

function submitForm(list: string) {
  return editingList.value === list ? saveEdit(list) : addItem(list)
}

async function startEditItem(item: ListItem) {
  const form = getForm(item.list)
  Object.assign(form, {
    list: item.list,
    title: item.title,
    url: item.url ?? '',
    notes: item.notes ?? '',
    priority: item.priority ?? null,
  })
  editingItemId.value = item.id
  tab.value = item.list
}

async function toggleDone(id: number, done: boolean) {
  try {
    await store.toggleDone(id, done)
  } catch {
    message.error('Failed to update item')
  }
}

async function deleteItem(id: number) {
  if (editingItemId.value === id) editingItemId.value = null
  try {
    await store.deleteItem(id)
  } catch {
    message.error('Failed to delete item')
  }
}

function onDragStart(event: DragEvent, id: number) {
  draggingId.value = id
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'
}

function onDragOver(event: DragEvent, id: number) {
  event.preventDefault()
  if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
  if (draggingId.value !== id) dragOverId.value = id
}

function onDragEnd() {
  draggingId.value = null
  dragOverId.value = null
}

async function onDrop(event: DragEvent, list: string, targetId: number) {
  event.preventDefault()
  const fromId = draggingId.value
  draggingId.value = null
  dragOverId.value = null
  if (fromId === null || fromId === targetId) return
  await reorder(list, fromId, targetId)
}

async function moveItem(list: string, id: number, direction: 'up' | 'down') {
  const listItems = listItemsFor(list)
  const idx = listItems.findIndex((i) => i.id === id)
  const targetIdx = direction === 'up' ? idx - 1 : idx + 1
  if (targetIdx < 0 || targetIdx >= listItems.length) return
  await reorder(list, id, listItems[targetIdx].id)
}

async function reorder(list: string, fromId: number, toId: number) {
  const listItems = listItemsFor(list)
  const fromIdx = listItems.findIndex((i) => i.id === fromId)
  const toIdx = listItems.findIndex((i) => i.id === toId)
  if (fromIdx === -1 || toIdx === -1) return
  const reordered = [...listItems]
  const [moved] = reordered.splice(fromIdx, 1)
  reordered.splice(toIdx, 0, moved)
  try {
    await store.reorderItems(
      list,
      reordered.map((i) => i.id),
    )
  } catch {
    message.error('Failed to reorder items')
    await store.fetchAll()
  }
}
</script>

<template>
  <RouteShell>
    <section class="mx-auto flex max-w-3xl flex-col gap-6 pb-6">
      <header class="flex flex-wrap items-end gap-3">
        <div class="min-w-[240px] flex-1">
          <h1 class="page-title">Lists</h1>
          <p class="m-0 mt-1.5 text-ink-muted">Lists are yours and the agents’ to add to.</p>
        </div>
        <button
          v-if="store.listNames.length > 0"
          type="button"
          class="min-h-11 rounded-[10px] border border-border-control px-4 font-medium text-ink hover:bg-surface-hover sm:min-h-10"
          @click="showCreateModal = true"
        >
          New list
        </button>
      </header>

      <div v-if="store.loading" class="flex items-center justify-center py-12">
        <NSpin aria-label="Loading…" />
      </div>
      <div v-else-if="store.loadError" class="flex flex-col items-center gap-3 py-12">
        <p class="m-0 text-sm text-status-alert">{{ store.loadError }}</p>
        <NButton size="small" @click="store.fetchAll()">Try again</NButton>
      </div>

      <template v-else>
        <section v-if="store.listNames.length > 0" aria-labelledby="list-heading" class="flex flex-col gap-3.5">
          <ListChips v-model="tab" :lists="chips" />
          <div
            class="flex flex-col gap-1 rounded-2xl border border-border-subtle bg-surface-raised p-4 sm:p-5"
            data-testid="list-card"
          >
            <h2 id="list-heading" class="m-0 mb-1.5 font-serif text-[22px] font-medium text-ink">
              {{ displayName(tab) }}
            </h2>
            <p v-if="selectedItems.length === 0" class="m-0 border-t border-border-divider py-4 text-ink-muted">
              {{ displayName(tab) }} is empty
            </p>
            <ListItemRow
              v-for="(item, idx) in selectedItems"
              :key="item.id"
              :item="item"
              :is-editing="editingItemId === item.id"
              :dragging-id="draggingId"
              :drag-over-id="dragOverId"
              :is-first="idx === 0"
              :is-last="idx === selectedItems.length - 1"
              @toggle="toggleDone"
              @edit="startEditItem"
              @delete="deleteItem"
              @dragstart="onDragStart"
              @dragover="onDragOver"
              @dragend="onDragEnd"
              @drop="(e, id) => onDrop(e, tab, id)"
              @move-up="(id) => moveItem(tab, id, 'up')"
              @move-down="(id) => moveItem(tab, id, 'down')"
            />
            <ListItemComposer
              :form="getForm(tab)"
              :list-label="displayName(tab)"
              :editing="editingList === tab"
              :saving="!!saving[tab]"
              @submit="submitForm(tab)"
              @cancel="cancelEdit(tab)"
            />
          </div>
        </section>

        <div
          v-if="store.listNames.length === 0"
          class="flex flex-col items-center gap-3 rounded-2xl border border-border-subtle bg-surface-raised px-4 py-12"
        >
          <p class="m-0 text-ink-muted">No lists yet</p>
          <NButton type="primary" @click="showCreateModal = true">Create your first list</NButton>
        </div>

        <CreateListModal v-model:show="showCreateModal" @created="tab = $event" />
      </template>
    </section>
  </RouteShell>
</template>
